/**
 * Mise à jour de l'application installée
 *
 * Play met bien à jour les applications tout seul, mais à son rythme : le
 * téléphone interroge le Store périodiquement, et une version publiée le soir
 * peut n'arriver que le lendemain. Pour une application qu'on livre plusieurs
 * fois par jour, c'est trop lent — on se retrouve à regarder un écran corrigé
 * depuis des heures en croyant que le correctif n'est pas parti.
 *
 * On demande donc à Play, **à chaque lancement**, s'il existe une version plus
 * récente, et on déclenche son écran de mise à jour. C'est le mécanisme déjà en
 * service dans l'autre application de la maison.
 *
 * Trois voies, de la plus directe à la moins :
 *
 *   immédiate  — Play affiche son écran bloquant, met à jour, relance ;
 *   souple     — téléchargement en arrière-plan, puis installation ;
 *   à défaut   — on ouvre simplement la fiche du Store.
 *
 * Meilleur effort de bout en bout : hors Play (installation par APK), hors
 * ligne, ou si l'utilisateur refuse, la fonction se tait. Une mise à jour ratée
 * ne doit jamais empêcher d'ouvrir l'application.
 */

import { Capacitor } from '@capacitor/core';
import { AppUpdate, AppUpdateAvailability } from '@capawesome/capacitor-app-update';

export interface EtatMiseAJour {
  disponible: boolean;
  versionInstallee: string | null;
  versionDisponible: string | null;
  message: string | null;
}

/** Interroge Play sans rien déclencher — pour l'afficher dans les réglages. */
export async function etatMiseAJour(): Promise<EtatMiseAJour> {
  const neutre: EtatMiseAJour = {
    disponible: false,
    versionInstallee: null,
    versionDisponible: null,
    message: null,
  };
  if (!Capacitor.isNativePlatform()) {
    return { ...neutre, message: 'Sans objet sur le web : la page se recharge d’elle-même.' };
  }
  try {
    const info = await AppUpdate.getAppUpdateInfo();
    return {
      disponible: info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE,
      versionInstallee: info.currentVersionName ?? null,
      versionDisponible: info.availableVersionName ?? null,
      message: null,
    };
  } catch (e) {
    // Typiquement : application installée hors Play, ou Play indisponible.
    return { ...neutre, message: (e as Error).message };
  }
}

/**
 * Propose la mise à jour si Play en connaît une. Rend ce qui a été tenté, pour
 * que l'écran des réglages puisse le dire plutôt que de rester muet.
 */
export async function proposeMiseAJour(): Promise<string> {
  if (!Capacitor.isNativePlatform()) return 'sans objet';
  try {
    const info = await AppUpdate.getAppUpdateInfo();
    if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
      return 'à jour';
    }
    if (info.immediateUpdateAllowed) {
      await AppUpdate.performImmediateUpdate();
      return 'mise à jour immédiate';
    }
    if (info.flexibleUpdateAllowed) {
      await AppUpdate.startFlexibleUpdate();
      await AppUpdate.completeFlexibleUpdate();
      return 'mise à jour souple';
    }
    await AppUpdate.openAppStore();
    return 'fiche du Store ouverte';
  } catch (e) {
    return `impossible : ${(e as Error).message}`;
  }
}
